import { isRemoteTrack, isUndefined } from './../utils/utils';
import {
  DATA_FREEZE_TIMING,
  KIBANA_EVENT,
  NAME
} from '../common/constants';
import { performanceNow } from '../utils/utils';
import DurationItem from '../metrics/duration-item';
import eventLogger from '../utils/log/event-log';
import { IRemoteTrack, IRoom, loggerManager, MULTI_VIDEO_DATA_TYPE, PlayerState, RemoteStreamType, RemoteTrack, RemoteTrackEvent, RemoteVideoTrack, VideoPlayer } from '..';
import INNER_EVENT, { InnerEventTypes } from '../inner-emitter/inner-event';
import innerEmitter from '../inner-emitter/inner-emitter';
import { isRequestVideoFrameCallbackSupported } from '../common/rtc-detection';
import { getOSString } from '../utils/environment';

enum FreezeType {
  RENDER = 'render',
  DATA = 'data'
}

class MonitorFreezeData {
  renderFreezeMap: Map<string, { userId: string, type: RemoteStreamType, duration: number; }> = new Map();
  dataFreezeMap: Map<string, { userId: string, type: RemoteStreamType, duration: number; }> = new Map();

  get(key: string, type?: FreezeType) {
    const renderFreeze = this.renderFreezeMap.get(key);
    const dataFreeze = this.dataFreezeMap.get(key);
    if (type) {
      return type === FreezeType.DATA ? dataFreeze : renderFreeze;
    }
    // Chrome 144+ 当帧未收到时不再抛出 track mute 事件: https://chromium-review.googlesource.com/c/chromium/src/+/7016808
    // 优先上报卡顿最大值
    if (renderFreeze && dataFreeze) {
      return renderFreeze.duration > dataFreeze.duration ? renderFreeze : dataFreeze;
    }
    return renderFreeze || dataFreeze;
  }

  set(key: string, value: { userId: string, type: RemoteStreamType, duration: number; }, type: FreezeType) {
    if (type === FreezeType.DATA) {
      this.dataFreezeMap.set(key, value);
    } else {
      this.renderFreezeMap.set(key, value);
    }
  }

  clear() {
    this.renderFreezeMap.clear();
    this.dataFreezeMap.clear();
  }
}

/**
 * bad case 探测器
 * 1. 检测黑屏，用于统计黑屏率
 * 2. 检测视频数据卡顿和渲染卡顿，用于统计视频卡顿率
 * @class BadCaseDetector
 */
class BadCaseDetector {
  private _room: IRoom;
  // 渲染卡顿的 Map
  private _renderFreezeMap: Map<string, {  userId: string, type: RemoteStreamType, renderFreezeTotal: number, count: number, exactRenderFreezeTotal: number; } > = new Map();
  // 记录通话过程中，调用了 remoteStream.play 接口的远端视频流
  // Map<userId_streamType, isPlayingFired>
  // 作用：若 stream.play 调用了，但是通话全程没触发 video playing 事件，则说明视频流黑屏
  private _isVideoPlayingEventFiredMap: Map<string, boolean> = new Map();
  // 数据流卡顿的Map<userId_type, { userId, type, durationItemList: DurationItem[] }>
  // Map 记录视频数据卡顿：通话过程中，video track 从 muted 到 unmuted 的时间间隔大于500ms，则记为卡顿
  private _dataFreezeMap: Map<string, { userId: string, type: RemoteStreamType, durationItemList: DurationItem[], isFreezing: () => boolean; }> = new Map();
  // 用于每 2s 上报到 monitor，上报完成就delete
  private _monitorFreezeData = new MonitorFreezeData();
  private _eventMap: Map<INNER_EVENT, (...args: any) => void> = new Map();
  // 用来判断上行编码失败，表现为码率帧率持续为 0，且没有抛出 encode failed 事件
  private _videoEncodeFailedCount = 0;
  private _audioEncodeFailedCount = 0;
  private _encodeFailedThreshold = 3;
  private ABNORMAL_TIME_LOWER_LIMIT = 3000;
  private ABNORMAL_TIME_UPPER_LIMIT = 5000;
  // 视频异常时间戳
  private _videoAbnormalTimestampMap: Map<string, number> = new Map();
  private _remoteVideoAbnormalTimestampMap: Map<string, number> = new Map();
  // 解码异常连续检测计数
  private _decodeFailedCountMap: Map<string, number> = new Map();
  private _decodeFailedThreshold = 3;
  // 标记已触发解码失败事件的 key，避免重复触发
  private _decodeFailedFiredSet: Set<string> = new Set();

  // 音频异常时间戳
  private _audioAbnormalTimestampMap: Map<string, number> = new Map();
  private eventListenerMap: Map<string, { onVisibilityChange: (event: any) => void }> = new Map();

  constructor(options: { room: IRoom; }) {
    this._room = options.room;
    this.installEvents();
    // @ts-ignore 测试用：控制台调用 window.__forceDecode0() 开启，window.__forceDecode0(false) 关闭
    // if (typeof window !== 'undefined' && !window.__forceDecode0) {
    //   // @ts-ignore
    //   window.__forceDecode0 = (enable = true) => {
    //     // @ts-ignore
    //     window.__forceDecode0Enabled = enable;
    //     // eslint-disable-next-line no-console
    //     console.warn(`[BadCaseDetector] forceDecode0 ${enable ? 'enabled' : 'disabled'}, uint32_video_dec_fps will be ${enable ? 'forced to 0' : 'normal'}`);
    //   };
    //   // @ts-ignore
    //   window.__forceDecode0Enabled = false;
    // }
  }

  getRenderFreezeMap() {
    return this._renderFreezeMap;
  }

  getDataFreezeMap() {
    return this._dataFreezeMap;
  }

  installEvents() {
    this._eventMap
      .set(INNER_EVENT.LEAVE_SUCCESS, ({ room }: InnerEventTypes[INNER_EVENT.LEAVE_SUCCESS][0]) => {
        if (this.hitTest(room)) this.stop();
      })
      .set(INNER_EVENT.SWITCH_ROOM_SUCCESS, ({ room }: InnerEventTypes[INNER_EVENT.SWITCH_ROOM_SUCCESS][0]) => {
        if (this.hitTest(room)) this.stop();
      })
      .set(INNER_EVENT.PLAY_TRACK_START, this.onPlayTrackStart)
      .set(INNER_EVENT.UNSUBSCRIBE_SUCCESS, ({ room, streamType, remotePublishedUser }: InnerEventTypes[INNER_EVENT.UNSUBSCRIBE_SUCCESS][0]) => {
        if (!this.hitTest(room)) return;
        const { userId } = remotePublishedUser;
        const key = `${userId}_${streamType}`;
        this.stopDataFreeze({ key, userId, type: streamType });
      })
      .set(INNER_EVENT.REMOTE_PUBLISH_STATE_CHANGED, ({ room, prevMuteState, muteState }: InnerEventTypes[INNER_EVENT.REMOTE_PUBLISH_STATE_CHANGED][0]) => {
        if (!this.hitTest(room)) return;
        const { userId } = muteState;
        if (prevMuteState.hasVideo && !muteState.hasVideo) {
          const streamType = RemoteStreamType.Main;
          const key = `${muteState.userId}_${streamType}`;
          this.stopDataFreeze({ key, userId, type: streamType });
        }
        if (prevMuteState.hasAuxiliary && !muteState.hasAuxiliary) {
          const streamType = RemoteStreamType.Aux;
          const key = `${muteState.userId}_${streamType}`;
          this.stopDataFreeze({ key, userId, type: streamType });
        }
      })
      .set(INNER_EVENT.PLAYER_STATE_CHANGED, ({ track, state, reason, type }: InnerEventTypes[INNER_EVENT.PLAYER_STATE_CHANGED][0]) => {
        if (!track.isRemote || !track.room || !this.hitTest(track.room) || type !== NAME.VIDEO) return;
        if (state === PlayerState.PLAYING) {
          const key = `${track.userId}_${track.streamType}`;
          this._isVideoPlayingEventFiredMap.set(key, true);
        }
        if (reason === NAME.MUTE) {
          this.onVideoTrackMuted(track as RemoteTrack<'video'>);
        } else if (reason === NAME.UNMUTE) {
          this.onVideoTrackUnmuted(track as RemoteTrack<'video'>);
        }
      })
      .set(INNER_EVENT.HEARTBEAT_REPORT, this.onHearBeatReport)
      .set(INNER_EVENT.REMOTE_VIDEO_PLAY_START, this.onRemoteVideoPlayStart)
      .set(INNER_EVENT.REMOTE_VIDEO_PLAY_FINISH, this.onRemoteVideoPlayEnd);

    this._eventMap.forEach((listener, eventName) => innerEmitter.on(eventName, listener, this));
  }

  uninstallEvents() {
    this._eventMap.forEach((listener, eventName) => innerEmitter.off(eventName, listener, this));
    this._eventMap.clear();
  }

  stop() {
    this._renderFreezeMap.clear();
    this._dataFreezeMap.clear();
    this._isVideoPlayingEventFiredMap.clear();
    this._decodeFailedCountMap.clear();
    this._decodeFailedFiredSet.clear();
  }

  /**
   * video muted 超过 500ms 记为一次数据卡顿
   * @memberof BadCaseDetector
   */
  onVideoTrackMuted(track: RemoteTrack<'video'>) {
    if (!track.isSubscribed) return;
    const { userId, streamType } = track;
    const key = `${userId}_${streamType}`;

    const dataFreeze = this._dataFreezeMap.get(key);
    const durationItem = new DurationItem();
    if (dataFreeze) {
      dataFreeze.durationItemList.push(durationItem);
    } else {
      // 没有卡顿过，map 里初始化
      this._dataFreezeMap.set(key, {
        userId,
        type: streamType,
        durationItemList: [durationItem],
        isFreezing() {
          const item = this.durationItemList[this.durationItemList.length - 1];
          return item && item.endTime === 0;
        }
      });
    }
  }

  // 数据流结束卡顿
  onVideoTrackUnmuted(track: RemoteTrack<'video'>) {
    if (!track.isSubscribed) return;
    const { userId, streamType } = track;
    const key = `${userId}_${streamType}`;
    this.stopDataFreeze({ key, userId, type: streamType });
  }

  onHearBeatReport({ room, report }: InnerEventTypes[INNER_EVENT.HEARTBEAT_REPORT][0]) {
    if (!this.hitTest(room)) return;
    this.localMediaTrackDetector(report);
    this.remoteMediaTrackDetector(report);
  }
  remoteMediaTrackDetector(report: HearBeatReport) {
    // @ts-ignore 测试用：在控制台调用 window.__forceDecode0() 将 uint32_video_dec_fps 强制设为 0，用于触发硬解异常检测
    // if (window.__forceDecode0Enabled) {
    //   report.msg_down_stream_info.forEach(streamInfo => {
    //     streamInfo.msg_video_status.forEach(videoStatus => {
    //       videoStatus.uint32_video_dec_fps = 0;
    //     });
    //   });
    // }
    if (report.msg_down_stream_info.length > 0) {
      report.msg_down_stream_info.forEach(streamInfo => {
        if (streamInfo.msg_video_status.length === 0) return;
        const userId = streamInfo.msg_user_info.str_identifier;
        const remoteVideoTrack = this._room.remotePublishedUserMap.get(userId)?.remoteVideoTrack;
        streamInfo.msg_video_status.forEach(videoStatus => {
          // 下行视频组帧异常，表现：码率正常，帧率为0， videoTrack muted
          const now = performanceNow();
          if (videoStatus.uint32_video_codec_bitrate !== undefined && videoStatus.uint32_video_codec_bitrate > 0 &&
            videoStatus.uint32_video_receive_fps === 0 && remoteVideoTrack?.muted) {
            if (!this._remoteVideoAbnormalTimestampMap.has(`${userId}-decode`)) {
              this._remoteVideoAbnormalTimestampMap.set(`${userId}-decode`, now);
            } else {
              const startTimestamp = this._remoteVideoAbnormalTimestampMap.get(`${userId}-decode`);
              if (startTimestamp) {
                if (now - startTimestamp > this.ABNORMAL_TIME_LOWER_LIMIT && now - startTimestamp < this.ABNORMAL_TIME_UPPER_LIMIT) {
                  eventLogger.uploadEvent({ userId: this._room.userId, log: `stat-${KIBANA_EVENT.VIDEO_FRAME_ASSEMBLE_FAILED_DURING_CALL}` });
                }
              }
            }
          } else {
            const startTimestamp = this._remoteVideoAbnormalTimestampMap.get(`${userId}-decode`);
            if (startTimestamp) {
              if (now - startTimestamp >= this.ABNORMAL_TIME_UPPER_LIMIT) {
                eventLogger.uploadEvent({ userId: this._room.userId, log: `stat-${KIBANA_EVENT.VIDEO_FRAME_ASSEMBLE_RESUME_DURING_CALL}` });
                this._remoteVideoAbnormalTimestampMap.delete(`${userId}-decode`);
              }
            }
          }
          if (isUndefined(videoStatus.uint32_video_codec_bitrate) || isUndefined(videoStatus.uint32_video_receive_fps) || isUndefined(videoStatus.uint32_video_dec_fps)) return;
          const isMainStream = videoStatus.uint32_video_stream_type === MULTI_VIDEO_DATA_TYPE.MULTI_DATA_BIG_IMG;
          const decodeKey = this.getDecodeFailedKey(userId, isMainStream ? RemoteStreamType.Main : RemoteStreamType.Aux);
          // 下行解码异常，表现：码率正常，接收帧率正常，解码帧率为0
          if (videoStatus.uint32_video_codec_bitrate > 500000 && videoStatus.uint32_video_receive_fps > 0 && videoStatus.uint32_video_dec_fps === 0) {
            // 已触发过则不再重复检测，等恢复或上层重置后再检测
            if (!this._decodeFailedFiredSet.has(decodeKey)) {
              const count = (this._decodeFailedCountMap.get(decodeKey) || 0) + 1;
              this._decodeFailedCountMap.set(decodeKey, count);
              if (count >= this._decodeFailedThreshold) {
                eventLogger.uploadEvent({ userId: this._room.userId, log: `stat-${KIBANA_EVENT.VIDEO_DECODE_FAILED_DURING_CALL}-${getOSString()}` });
                this._decodeFailedCountMap.delete(decodeKey);
                this._decodeFailedFiredSet.add(decodeKey);
                const remotePublishedUser = this._room.remotePublishedUserMap.get(userId);
                if (remotePublishedUser) {
                  const remoteVideoTrack = videoStatus.uint32_video_stream_type === MULTI_VIDEO_DATA_TYPE.MULTI_DATA_BIG_IMG ? remotePublishedUser.remoteVideoTrack : remotePublishedUser.remoteAuxiliaryTrack;
                  if (remoteVideoTrack) {
                    remoteVideoTrack.log.warn('decode failed during call');
                    remoteVideoTrack.emit(RemoteTrackEvent.DECODE_FAILED_DURING_CALL);
                  }
                }
              }
            }
          } else if (videoStatus.uint32_video_dec_fps > 0) {
            // 解码帧率恢复才认为真正恢复，避免码率下降等场景误触恢复
            if (this._decodeFailedFiredSet.has(decodeKey) || this._decodeFailedCountMap.has(decodeKey)) {
              if (this._decodeFailedFiredSet.has(decodeKey)) {
                eventLogger.uploadEvent({ userId: this._room.userId, log: `stat-${KIBANA_EVENT.VIDEO_DECODE_RESUME_DURING_CALL}-${getOSString()}` });
                const remotePublishedUser = this._room.remotePublishedUserMap.get(userId);
                if (remotePublishedUser) {
                  const remoteVideoTrack = videoStatus.uint32_video_stream_type === MULTI_VIDEO_DATA_TYPE.MULTI_DATA_BIG_IMG ? remotePublishedUser.remoteVideoTrack : remotePublishedUser.remoteAuxiliaryTrack;
                  if (remoteVideoTrack) {
                    remoteVideoTrack.emit(RemoteTrackEvent.DECODE_RESUME_DURING_CALL);
                  }
                }
              }
              this._decodeFailedCountMap.delete(decodeKey);
              this._decodeFailedFiredSet.delete(decodeKey);
            }
          }
        });
      });
    }
  }
  localMediaTrackDetector(report: HearBeatReport) {
    if (report.msg_up_stream_info.msg_video_status) {
      const videoStatus = report.msg_up_stream_info.msg_video_status;
      const localVideoTrack = Array.from(this._room.localTracks).find(track => track.kind === 'video' && !track.isScreen);
      const bytesSent = localVideoTrack?.stat.bytesSent || 0;
      if (localVideoTrack?.isMediaTrackActive === false || bytesSent <= 0 || localVideoTrack?.isUseCustomSource) {
        return;
      }
      videoStatus.forEach(videoStat => {
        const now = performanceNow();
        if (videoStat.uint32_video_stream_type === MULTI_VIDEO_DATA_TYPE.MULTI_DATA_BIG_IMG) {
          if (videoStat.uint32_video_capture_fps !== 0 && videoStat.uint32_video_codec_bitrate === 0 && videoStat.uint32_video_enc_fps === 0 && localVideoTrack?.isPublished) {
            // - 将统计异常次数改为统计异常时间戳，两次异常时间戳的间隔在 3～5s 内属于异常上报范围。超过5s，属于持续异常，或者主线程卡顿，获取监控数据异常，不用上报。
            if (!this._videoAbnormalTimestampMap.has('local-encode')) {
              this._videoAbnormalTimestampMap.set('local-encode', now);
            } else {
              const startTimestamp = this._videoAbnormalTimestampMap.get('local-encode');
              if (startTimestamp) {
                if (now - startTimestamp > this.ABNORMAL_TIME_LOWER_LIMIT && now - startTimestamp < this.ABNORMAL_TIME_UPPER_LIMIT) {
                  eventLogger.uploadEvent({ userId: this._room.userId, log: `stat-${KIBANA_EVENT.VIDEO_ENCODE_FAILED_DURING_CALL}` });
                }
              }
            }
          } else {
            const startTimestamp = this._videoAbnormalTimestampMap.get('local-encode');
            if (startTimestamp) {
              if (now - startTimestamp >= this.ABNORMAL_TIME_UPPER_LIMIT) {
                eventLogger.uploadEvent({ userId: this._room.userId, log: `stat-${KIBANA_EVENT.VIDEO_ENCODE_RESUME_DURING_CALL}` });
              }
            }
            this._videoAbnormalTimestampMap.delete('local-encode');
          }
        }
      });
    }
    if (report.msg_up_stream_info.msg_audio_status) {
      const audioStatus = report.msg_up_stream_info.msg_audio_status;
      const localAudioTrack = Array.from(this._room.localTracks).find(track => track.kind === 'audio');
      const bytesSent = localAudioTrack?.stat.bytesSent || 0;
      if (localAudioTrack?.isMediaTrackActive === false || bytesSent <= 0 || localAudioTrack?.isUseCustomSource) {
        return;
      }
      const now = performanceNow();
      if (audioStatus.uint32_audio_codec_bitrate === 0 && localAudioTrack?.isPublished) {
        if (!this._audioAbnormalTimestampMap.has('local-encode')) {
          this._audioAbnormalTimestampMap.set('local-encode', now);
        } else {
          const startTimestamp = this._audioAbnormalTimestampMap.get('local-encode');
          if (startTimestamp) {
            if (now - startTimestamp > this.ABNORMAL_TIME_LOWER_LIMIT && now - startTimestamp < this.ABNORMAL_TIME_UPPER_LIMIT) {
              eventLogger.uploadEvent({ userId: this._room.userId, log: `stat-${KIBANA_EVENT.AUDIO_ENCODE_FAILED_DURING_CALL}` });
            }
          }
        }
      } else {
        const startTimestamp = this._audioAbnormalTimestampMap.get('local-encode');
        if (startTimestamp) {
          if (now - startTimestamp >= this.ABNORMAL_TIME_UPPER_LIMIT) {
            eventLogger.uploadEvent({ userId: this._room.userId, log: `stat-${KIBANA_EVENT.AUDIO_ENCODE_RESUME_DURING_CALL}` });
          }
        }
        this._audioAbnormalTimestampMap.delete('local-encode');
      }
    }
  }

  stopDataFreeze({ key, userId, type }: { key: string; userId: string; type: RemoteStreamType; }) {
    const dataFreeze = this._dataFreezeMap.get(key);

    if (!dataFreeze || !dataFreeze.isFreezing()) {
      return;
    }

    const durationItem = dataFreeze.durationItemList[dataFreeze.durationItemList.length - 1];
    durationItem.stop();
    const duration = durationItem.getDuration();
    // 时长大于500ms 记为一次数据卡顿
    if (duration > DATA_FREEZE_TIMING) {
      const item = this._monitorFreezeData.get(key, FreezeType.DATA);
      this._monitorFreezeData.set(key, {
        userId,
        type,
        duration: item ? item.duration + duration : duration
      }, FreezeType.DATA);
    } else {
      dataFreeze.durationItemList.pop();
    }
  }

  getTotalDuration(durationItemList: DurationItem[]) {
    return durationItemList.reduce((result, durationItem) => {
      const duration = durationItem.getDuration();
      // 对于 5s 以上的单次卡顿，统一记录为单次卡顿 5s
      // ref: http://tapd.oa.com/LiteAV_TRTC/prong/stories/view/1020403092864782295
      return result + Math.min(duration, 5000);
    }, 0);
  }

  /**
   * 只记录远端视频流，当调用了 playVideo 接口，但通话全程没触发 playing，则说明是黑屏流。
   * @param {Object} event
   * @memberof BadCaseDetector
   */
  onPlayTrackStart({ track }: InnerEventTypes[INNER_EVENT.PLAY_TRACK_START][0]) {
    if (!track.isRemote || !this.hitTest(track.room!) || track.kind !== NAME.VIDEO || !(track as IRemoteTrack).isRemotePublished) {
      return;
    }
    const key = `${track.userId}_${track.streamType}`;
    // 该远端用户的主流/辅流，已经播放过了，再次触发该回调说明重新订阅或远端重新推流，需更新 remoteStream
    if (!this._isVideoPlayingEventFiredMap.has(key)) {
      // 第一次播放该远端用户的主流/辅流
      this._isVideoPlayingEventFiredMap.set(key, false);
    }
  }

  // 获取某条流的数据卡顿
  getDataFreezeDuration(key: string) {
    const result = {
      dataFreeze: 0,
      count: 0
    };
    const dataFreeze = this._dataFreezeMap.get(key);
    if (dataFreeze) {
      if (dataFreeze.isFreezing()) {
        const durationItem = dataFreeze.durationItemList[dataFreeze.durationItemList.length - 1];
        durationItem.stop();
        const duration = durationItem.getDuration();
        if (duration < DATA_FREEZE_TIMING) dataFreeze.durationItemList.pop();
      }
      result.dataFreeze = this.getTotalDuration(dataFreeze.durationItemList);
      result.count = dataFreeze.durationItemList.length;
    }
    return result;
  }

  // 获取某条流的渲染卡顿
  getRenderFreezeDuration(key: string) {
    const result = {
      renderFreeze: 0,
      exactRenderFreeze: 0,
      count: 0
    };
    const freezeItem = this._renderFreezeMap.get(key);
    if (freezeItem) {
      result.renderFreeze = freezeItem.renderFreezeTotal;
      result.exactRenderFreeze = freezeItem.exactRenderFreezeTotal;
      result.count = freezeItem.count;
    }
    return result;
  }

  getMonitorFreeze() {
    return this._monitorFreezeData;
  }

  /**
   * 判断是否是黑屏流
   * @param {String} key `${userId}_${streamType}`
   * @return {*}
   * @memberof BadCaseDetector
   */
  isBlackStream(key: string) {
    if (this._isVideoPlayingEventFiredMap.has(key)) {
      const isPlayingFired = this._isVideoPlayingEventFiredMap.get(key);
      // 没播放成功的流是 黑屏流
      return !isPlayingFired;
    }
    return false;
  }

  private calcExpectedFrameInterval(frameCounts: number[], delays: number[]): number {
    if (frameCounts.length === 0 || delays.length === 0) return Infinity;
    let totalFrames = 0;
    let totalTime = 0;
    for (let i = 0; i < delays.length; i++) {
      totalFrames += frameCounts[i];
      totalTime += delays[i];
    }
    if (totalFrames === 0) return Infinity;
    return Math.floor(totalTime / totalFrames);
  }

  /**
   * 使用 requestVideoFrameCallback 来检测视频卡顿
   * 包括：渲染卡顿、数据上报
   */
  onRemoteVideoPlayStart({ track, player }: { track: RemoteVideoTrack, player: VideoPlayer }) {
    if (!isRequestVideoFrameCallbackSupported()) return;
    let prevPresentationTime = 0;
    let prevPresentedFrames = 0;
    const MAX_WINDOW_SIZE = 30;
    const timeDiffHistory: number[] = [];
    const frameDiffHistory: number[] = [];
    // 记录切后台/前台的时间戳，用于过滤后台期间的卡顿检测
    // known issue：iOS/Safari 切后台时 RVFC 仍会回调但速度变慢，会导致误检测为卡顿
    let lastVisibleTime = performanceNow();
    let lastHiddenTime = 0;
    const onVisibilityChange = () => {
      if (document.hidden) {
        lastHiddenTime = performanceNow();
      } else {
        lastVisibleTime = performanceNow();
        // 切回前台时，重置状态避免上报数据不准确
        prevPresentationTime = 0;
        prevPresentedFrames = 0;
        timeDiffHistory.length = 0;
        frameDiffHistory.length = 0;
      }
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    const onVideoFrame = (time: DOMHighResTimeStamp, metadata: VideoFrameCallbackMetadata) => {
      if (prevPresentationTime) {
        const timeDiff = metadata.presentationTime - prevPresentationTime;
        const frameDiff = metadata.presentedFrames - prevPresentedFrames;
        timeDiffHistory.push(timeDiff);
        frameDiffHistory.push(frameDiff);
        if (frameDiffHistory.length > MAX_WINDOW_SIZE) {
          timeDiffHistory.shift();
          frameDiffHistory.shift();
        }

        const fps = track.decodeFPS;
        const expectedDisplaytimeDiff = (fps > 0 && fps <= 5) ? 600 + (1000 / fps) : 600;

        // 卡顿检测条件：
        // 1. 帧间隔超过阈值
        // 2. 当前在前台（lastVisibleTime >= lastHiddenTime）
        const isInForeground = lastVisibleTime >= lastHiddenTime;
        if (timeDiff > expectedDisplaytimeDiff && isInForeground) {
          // 对于 5s 以上的单次卡顿，统一记录为单次卡顿 5s
          // ref: http://tapd.oa.com/LiteAV_TRTC/prong/stories/view/1020403092864782295
          const freezeTime = Math.min(timeDiff, 5000);
          const key = `${track.userId}_${track.streamType}`;

          // 2s monitor 上报
          const monitorItem = this._monitorFreezeData.get(key, FreezeType.RENDER);
          if (monitorItem) {
            monitorItem.duration += freezeTime;
          } else {
            this._monitorFreezeData.set(key, {
              userId: track.userId,
              type: track.streamType,
              duration: freezeTime
            }, FreezeType.RENDER);
          }

          // 计算精确卡顿：期望帧间隔上限 66ms（≈15fps）
          // 真实卡顿 = 帧间隔 - (跳帧数 - 1) * 期望帧间隔，排除主线程卡顿导致 RVFC 跳帧的影响
          const expectedInterval = Math.min(66, this.calcExpectedFrameInterval(frameDiffHistory, timeDiffHistory));
          const exactRenderFreeze = Math.min(Math.max(0, timeDiff - (Math.max(frameDiff, 1) - 1) * expectedInterval), 5000);

          // 结束上报
          const endReportItem = this._renderFreezeMap.get(key);
          if (endReportItem) {
            endReportItem.renderFreezeTotal += freezeTime;
            endReportItem.exactRenderFreezeTotal += exactRenderFreeze;
            endReportItem.count += 1;
          } else {
            this._renderFreezeMap.set(key, {
              userId: track.userId,
              type: track.streamType,
              renderFreezeTotal: freezeTime,
              exactRenderFreezeTotal: exactRenderFreeze,
              count: 1
            });
          }
        }
      }
      prevPresentationTime = metadata.presentationTime;
      prevPresentedFrames = metadata.presentedFrames;
      player.element?.requestVideoFrameCallback(onVideoFrame);
    };
    player.element?.requestVideoFrameCallback(onVideoFrame);
    this.eventListenerMap.set(`${track.userId}_${track.streamType}`, {
      onVisibilityChange
    });
  }

  onRemoteVideoPlayEnd({ track, player }: { track: RemoteVideoTrack, player: VideoPlayer }) {
    const key = `${track.userId}_${track.streamType}`;
    const eventListener = this.eventListenerMap.get(key);
    if (eventListener) {
      document.removeEventListener('visibilitychange', eventListener.onVisibilityChange);
    }
  }

  resetMonitor() {
    this._monitorFreezeData.clear();
  }

  hitTest(room: IRoom) {
    return room === this._room;
  }
  private getDecodeFailedKey(userId: string, streamType: RemoteStreamType) {
    return `${userId}-${streamType}-decode-failed`;
  }

  /**
   * 重置指定用户的解码失败检测计数，供上层重试恢复后调用，使得重试后可以继续检测。
   * 注意：不清理 _decodeFailedFiredSet，保留它以便检测解码恢复事件（VIDEO_DECODE_RESUME_DURING_CALL）。
   */
  resetDecodeFailedDetection(userId: string, streamType?: RemoteStreamType) {
    if (streamType) {
      const decodeKey = this.getDecodeFailedKey(userId, streamType);
      this._decodeFailedCountMap.delete(decodeKey);
    } else {
      this._decodeFailedCountMap.delete(this.getDecodeFailedKey(userId, RemoteStreamType.Main));
      this._decodeFailedCountMap.delete(this.getDecodeFailedKey(userId, RemoteStreamType.Aux));
    }
  }
  destroy() {
    this.uninstallEvents();
  }
}

export default BadCaseDetector;
